import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { LuxAccordion, LuxTextInput, LuxIconSpinner, Color } from '@lux/wardrobe-lib'
import useDistributionAction from '@/app/hooks/useDistributionAction'
import { DistributionListProps } from '@/app/models/new-resource'
import Distribution from '../ActivityCreation/Distribution/DistributionList'
import { ForumUser } from '@/app/models/forum/new-forum.model'
import ForumDistributionUserItem from './ForumDistributionUserItem'
import { ProfileService } from '@/app/services/ProfileService'
import './ForumDistribution.scss'
export interface ForumDistributionProps {
    storeList?: { store_id: string }[]
    referenceStoreList?: any[]
    referenceThreadUserList?: ForumUser[]
    selectedUsers?: ForumUser[]
    isEdit?: boolean
    onSetDistribution: Dispatch<SetStateAction<any[]>>
    onSetUsers: Dispatch<SetStateAction<ForumUser[]>>
    onRemoveReferenceUser?: (id: string, enable?: boolean) => void
    tableConfigId: string
    smartFilter: any
    setSmartFilter: (value: any) => void
}

const ForumDistribution = ({
    onSetDistribution,
    onSetUsers,
    onRemoveReferenceUser,
    referenceStoreList,
    referenceThreadUserList,
    selectedUsers,
    storeList,
    isEdit,
    tableConfigId,
    smartFilter,
    setSmartFilter,
}: ForumDistributionProps) => {
    const intl = useIntl()
    const [customProps, setCustomProps] = useState<any>({})
    const [searchField, setSearchField] = useState<string>('')
    const [loading, setLoading] = useState<boolean>(false)
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const className = 'smart-modals-forum-distribution'
    const accordionTitle = useMemo(
        () =>
            intl.formatMessage({
                id: 'modals.new.activity.section.distribution',
                defaultMessage: 'Distribution',
            }),
        [intl]
    )
    const placeholder = useMemo(
        () =>
            intl.formatMessage({
                id: 'modals.new.forum.distribution.user.placeholder',
                defaultMessage: 'Search user',
            }),
        [intl]
    )
    const addPeopleLabel = useMemo(
        () =>
            intl.formatMessage({
                id: 'modals.new.forum.distribution.add.user',
                defaultMessage: 'Add people',
            }),
        [intl]
    )
    const noUsersSelectedLabel = useMemo(
        () =>
            intl.formatMessage({
                id: 'modals.new.forum.distribution.no.user.selected',
                defaultMessage: 'No people added yet',
            }),
        [intl]
    )

    const [distributionAction] = useDistributionAction()

    useEffect(() => {
        distributionAction.setDistributionModel((value) => ({
            ...value,
            selectedStoreList: {
                ...value.selectedStoreList,
                value: storeList || [],
            },
        }))
    }, [storeList])

    useEffect(() => {
        if (referenceStoreList) {
            distributionChangeHandler({
                ...distributionAction.distributionModel,
                selectedStoreList: {
                    ...distributionAction.distributionModel.selectedStoreList,
                    value: isEdit
                        ? referenceStoreList.filter((s) =>
                              storeList.find((st) => st.store_id === s.store_id)
                          )
                        : referenceStoreList,
                },
            })
            setCustomProps({
                customStoreList: referenceStoreList.map((s) => s.store_id),
            })
        }
    }, [referenceStoreList])

    useEffect(() => {
        onSetUsers(selectedUsers)
    }, [selectedUsers])

    const distributionChangeHandler = (form: DistributionListProps) => {
        distributionAction.setDistributionModel(form)
        onSetDistribution(form.selectedStoreList.value)
    }

    const onRemove = (id: string) => {
        const userIndex = selectedUsers.findIndex((u) => u?.userId === id)
        let newUsers = [...selectedUsers]
        newUsers.splice(userIndex, 1)
        onSetUsers(newUsers)
        if (!!referenceThreadUserList) {
            onRemoveReferenceUser(id)
        }
    }

    const onAdd = (id: string) => {
        onSetUsers([...selectedUsers, referenceThreadUserList.find((user) => user.userId === id)])
        onRemoveReferenceUser(id, true)
    }

    const onUserSearch = () => {
        if (!selectedUsers.find((user) => user.userId === searchField)) {
            if (!!referenceThreadUserList) {
                onSetUsers([
                    ...selectedUsers,
                    referenceThreadUserList.find((user) => user.userId === searchField),
                ])
                return
            }
            setLoading(true)
            ProfileService.GetSearchUserList(searchField)
                .then((res) => {
                    onSetUsers([...selectedUsers, ...res.data.userList])
                })
                .finally(() => {
                    setLoading(false)
                })
        }
    }
    const onResetInput = () => {
        setSearchField('')
    }
    const getInputIcon = () => {
        if (loading) return ''
        return searchField ? 'status-ko' : 'search'
    }

    const getUserList = () => {
        return referenceThreadUserList || selectedUsers
    }

    const onSetActiveIndex = () => {
        setIsOpen(true)
    }

    return (
        <LuxAccordion title={accordionTitle} setActiveIndex={onSetActiveIndex}>
            <Distribution
                tableConfigId={tableConfigId}
                form={distributionAction.distributionModel}
                onSetForm={distributionChangeHandler}
                smartFilter={smartFilter}
                setSmartFilter={setSmartFilter}
                {...customProps}
            />
            <div className={`${className}-user-search`}>
                {!referenceThreadUserList && (
                    <LuxTextInput
                        label={addPeopleLabel}
                        icon={getInputIcon()}
                        iconPosition={searchField ? 'right' : 'left'}
                        iconColor={searchField ? Color.GREY2 : Color.BLACK}
                        value={searchField}
                        placeholder={placeholder}
                        iconClick={onResetInput}
                        onEnter={onUserSearch}
                        onChangeCallback={setSearchField}
                    />
                )}
                {loading && (
                    <LuxIconSpinner size={16} color={Color.GREY2} classes="user-search-spinner" />
                )}
            </div>
            <div
                className={`${className}-user-list ${
                    selectedUsers.length > 3 ? 'has-shadow' : ''
                }`}>
                {!referenceThreadUserList?.length && !selectedUsers.length && (
                    <div className="text-mini">{noUsersSelectedLabel}</div>
                )}
                {getUserList().map((user) => (
                    <ForumDistributionUserItem
                        disabled={user.disabled}
                        key={user.userId}
                        avatarImage={user.attachment?.url}
                        {...user}
                        onRemove={onRemove}
                        onAdd={onAdd}
                    />
                ))}
            </div>
        </LuxAccordion>
    )
}

export default ForumDistribution
